import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { addWeeks } from 'date-fns';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';

import {
  formatDate,
  formatCurrency,
} from '#common/utils';

interface ShippingWindowOption {
  name?: string;
  weeks?: number | string;
  price?: number;
}

interface OrderItemDetailsProduct {
  id?: string | number;
  name?: string;
  shippingWindow?: Record<string, ShippingWindowOption>;
  customizations?: Record<string, { name?: string; price?: number }>;
  [key: string]: unknown;
}

export interface OrderItemDetailsItem {
  product: OrderItemDetailsProduct;
  barcodeItems?: Array<{ barcode: string; received?: boolean }>;
  inStock?: boolean;
  estimateShipDate?: string | number | Date;
  shipDatePending?: boolean;
  confirmDeadline?: string | number | Date | null;
  availableDate?: string | number | Date;
  selectedShippingWindow?: string;
  fromInventory?: boolean;
  collectionNames?: string[];
  size?: string;
  color?: string;
  wearDate?: string | number | Date;
  poNumber?: string;
  selectedCustomizations?: Record<string, unknown>;
  customizationNotes?: Record<string, string>;
  specialRequests?: string;
  [key: string]: unknown;
}

export interface OrderItemDetailsOrder {
  barcodeItems?: Array<{ barcode: string; received?: boolean }>;
  createdDate: string | number | Date;
  [key: string]: unknown;
}

interface OrderItemDetailsProps {
  item: OrderItemDetailsItem;
  order: OrderItemDetailsOrder;
  isInventory?: boolean;
  // cancelled rows never show the pending label
  cancelled?: boolean;
}

export default function OrderItemDetails(props: OrderItemDetailsProps) {
  const navigate = useNavigate();
  const { t } = useTranslation('order');
  const {
    item, order, isInventory, cancelled,
  } = props;
  const { product } = item;
  const timeZone = useSelector((s: { appConfig: { timeZone?: string } }) => s.appConfig.timeZone);

  let shippingWindowText = '';
  let estimateShipDateText = t('item.inStockShipNow');

  // Check if any of the item's barcodes match a received barcode in the order
  const isReceived = item.barcodeItems?.some((itemBarcode) =>
    order.barcodeItems?.some((orderBarcode) =>
      orderBarcode.barcode === itemBarcode.barcode && orderBarcode.received,
    )) || false;

  // the sweep moves cancelled items as-is, so they keep shipDatePending
  const pending = !!item.shipDatePending && !cancelled && !item.cancelledAt;
  if (pending) {
    // customized item: no computed date until an admin sets one
    estimateShipDateText = item.confirmDeadline
      ? t('item.shipDatePendingDeadline', { deadline: formatDate(item.confirmDeadline, '', { timeZone }) })
      : t('item.shipDateToBeConfirmed');
  } else if (item.shipDatePending) {
    // cancelled before a ship date was ever set
    estimateShipDateText = '-';
  } else if (isReceived || item.inStock) {
    // Item is either received or in stock
    estimateShipDateText = t('item.inStockShipNow');
  } else if (item.estimateShipDate) {
    // if the estimateShipDate is available the estimateShipDateText
    // will be rewritten by estimateShipDate
    estimateShipDateText = formatDate(new Date(item.estimateShipDate));
  } else if (item.availableDate) {
    // compatibility 1: the old data has availableDate
    estimateShipDateText = formatDate(new Date(item.availableDate));
  } else if (product.shippingWindow) {
    // compatibility 2: the old data hasnt availableDate
    const current = product.shippingWindow[item.selectedShippingWindow as string];
    if (current) {
      const weeks = Number(current.weeks);
      const estimateShipDate = addWeeks(new Date(order.createdDate), weeks);
      if (estimateShipDate > new Date()) {
        estimateShipDateText = formatDate(estimateShipDate);
      }
    }
  }

  if (!item.fromInventory && product.shippingWindow) {
    const current = product.shippingWindow[item.selectedShippingWindow as string];
    if (current) {
      shippingWindowText = t('item.shippingWindow', {
        name: current.name,
        weeks: current.weeks,
        price: formatCurrency(current.price as number),
      });
    }
  }

  return (
    <>
      <Box>
        <Link component="button" onClick={() => { void navigate(`/shop/add?productId=${product.id}`); }}>
          {product.name}
        </Link>
      </Box>
      <Box>
        <Typography variant="body2">
          <b>{t('item.collection')}</b>
          :
          {' '}
          {(item.collectionNames ?? []).join(', ')}
        </Typography>
      </Box>
      <Box>
        <Typography variant="body2">
          <b>{t('item.size')}</b>
          :
          {' '}
          {item.size}
        </Typography>
      </Box>
      <Box>
        <Typography variant="body2">
          <b>{t('item.color')}</b>
          :
          {' '}
          {item.color}
        </Typography>
      </Box>
      {!isInventory && (
        <>
          <Box>
            <Typography variant="body2">
              <b>{t('item.wearDate')}</b>
              :
              {' '}
              {formatDate(item.wearDate as string | number | Date)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2">
              <b>{t('item.poNumber')}</b>
              :
              {' '}
              {item.poNumber}
            </Typography>
          </Box>

          <Box>
            <Typography variant="body2">
              <b>{t('item.estimateShipDate')}</b>
              :
              {' '}
              {pending ? (
                <Box component="span" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                  {estimateShipDateText}
                </Box>
              ) : estimateShipDateText}
            </Typography>
          </Box>
        </>
      )}
      <Box>
        {!isInventory && shippingWindowText}
        {
          Object.keys(item.selectedCustomizations || {})
            .filter((k) => item.selectedCustomizations?.[k])
            .map((scIdx, idx) => {
              const custom = product.customizations?.[scIdx];
              return (
                <Box key={idx}>
                  <Typography variant="body2">
                    {custom?.name}
                    &nbsp;(+
                    {formatCurrency(custom?.price as number)}
                    ) &nbsp;
                    {item.customizationNotes?.[scIdx]}
                  </Typography>
                </Box>
              );
            })
        }
        {item.specialRequests ? (
          <Box>
            <Typography variant="body2">
              <b>{t('item.specialRequests')}</b>
              :
              {' '}
              {item.specialRequests}
            </Typography>
          </Box>
        ) : null}
      </Box>
    </>
  );
}
